import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import dayjs from "dayjs";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha, useTheme, lighten, darken } from "@mui/material/styles";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import TimerIcon from "@mui/icons-material/Timer";
import TimelapseIcon from "@mui/icons-material/Timelapse";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { fetchWorkspaceTaskAnalytics } from "../../../api/client";
import { formatDateTimePtBR } from "../../../utils/dateTime";
import { ensureNamespaceLoadedSync } from "../../../i18n/lazyNamespaces";
import { formatTaskDisplayName } from "../utils/taskDisplayName";

const BR = (x) => (Number.isFinite(x) ? x : Number(x ?? 0)).toLocaleString("pt-BR");

const secToHuman = (sec) => {
  const s = Math.max(0, Math.round(Number(sec || 0)));
  if (!s) return "0m";
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d${h ? ` ${h}h` : ""}`;
  if (h > 0) return `${h}h${m ? ` ${m}m` : ""}`;
  return `${m || 1}m`;
};

const hourLabel = (h) => `${String(h).padStart(2, "0")}:00`;

function peakHourSummary(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const max = Math.max(0, ...list.map((row) => Number(row.value) || 0));
  if (!max) return null;
  const hours = list.filter((row) => Number(row.value) === max).map((row) => hourLabel(row.hour));
  return { max, label: hours.join(" · ") };
}

const cardSx = {
  border: 1,
  borderColor: "divider",
  boxShadow: (th) =>
    th.palette.mode === "dark"
      ? `0 1px 4px ${alpha("#000", 0.35)}`
      : `0 1px 3px ${alpha(th.palette.common.black, 0.08)}`,
};

function resolveTaskStatusSummary(teamTasks, t) {
  const s = teamTasks?.summary || {};
  const n = (key) => {
    const v = Number(s[key]);
    return Number.isFinite(v) ? v : 0;
  };
  return [
    { key: "aFazer", label: t("userOperatorAnalytics.taskStatusTodo"), value: n("aFazer"), paletteKey: "pending", icon: HourglassEmptyIcon },
    { key: "emAndamento", label: t("userOperatorAnalytics.taskStatusDoing"), value: n("emAndamento"), paletteKey: "inProgress", icon: TimelapseIcon },
    { key: "paraDepois", label: t("userOperatorAnalytics.taskStatusLater"), value: n("paraDepois"), paletteKey: "future", icon: EventAvailableIcon },
    { key: "concluidas", label: t("userOperatorAnalytics.taskStatusDone"), value: n("concluidas"), paletteKey: "success", icon: CheckCircleOutlineIcon },
    { key: "atrasadas", label: t("userOperatorAnalytics.taskStatusOverdue"), value: n("atrasadas"), paletteKey: "unsuccessful", icon: WarningAmberIcon },
  ];
}

function KpiCard({ loading, icon, title, value, color, sub }) {
  if (loading) {
    return (
      <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
        <Skeleton variant="rounded" height={118} sx={{ width: "100%", borderRadius: 1 }} />
      </Grid>
    );
  }
  return (
    <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
      <Card elevation={1} sx={{ width: "100%", display: "flex", flexDirection: "column", ...cardSx }}>
        <CardContent sx={{ flex: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: color, width: 44, height: 44 }}>{icon}</Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" noWrap>
                {title}
              </Typography>
              <Typography variant="h4" sx={{ color, lineHeight: 1.15, fontWeight: 600 }}>
                {value}
              </Typography>
              {sub ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                  {sub}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
}

function HourDistributionCard({ title, hint, peakText, rows, maxValue, emptyLabel, colHour, colDistribution, colQty, tableSx }) {
  return (
    <Grid item xs={12} md={6}>
      <Card elevation={1} sx={{ height: "100%", ...cardSx }}>
        <CardContent sx={{ pt: 2, pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: peakText ? 0.5 : 1.5 }}>
            {hint}
          </Typography>
          {peakText ? (
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>{peakText}</Typography>
          ) : null}
          <TableContainer variant="outlined" sx={{ bgcolor: "background.paper", borderRadius: 1, maxHeight: 360 }}>
            <Table size="small" stickyHeader sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 64 }}>{colHour}</TableCell>
                  <TableCell>{colDistribution}</TableCell>
                  <TableCell align="right" sx={{ width: 72 }}>{colQty}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length ? (
                  rows.map((row) => (
                    <TableRow key={row.hour} hover>
                      <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>{hourLabel(row.hour)}</TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, (Number(row.value) / maxValue) * 100)}
                          sx={{
                            height: 10,
                            borderRadius: 1,
                            bgcolor: (th) => alpha(th.palette.primary.main, 0.08),
                            "& .MuiLinearProgress-bar": { borderRadius: 1 },
                          }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{row.value}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 3, color: "text.secondary" }}>
                      <Typography variant="body2">{emptyLabel}</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Grid>
  );
}

const formatDayBr = (day) => {
  const match = String(day || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return String(day || "");
  return `${match[3]}/${match[2]}`;
};

function DailyFlowChart({ title, hint, data, receivedLabel, completedLabel, receivedColor, completedColor }) {
  return (
    <Grid item xs={12}>
      <Card elevation={1} sx={cardSx}>
        <CardContent sx={{ pt: 2, pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>{hint}</Typography>
          <Box sx={{ width: "100%", height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" tickFormatter={formatDayBr} minTickGap={28} />
                <YAxis allowDecimals={false} width={36} />
                <ChartTooltip
                  labelFormatter={(label) => {
                    const match = String(label || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
                    return match ? `${match[3]}/${match[2]}/${match[1]}` : label;
                  }}
                />
                <Legend />
                <Line type="monotone" dataKey="received" name={receivedLabel} stroke={receivedColor} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="completed" name={completedLabel} stroke={completedColor} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
}

export function StageTimeAvgChart({
  rows = [],
  loading = false,
  title,
  hint,
  emptyLabel,
  axisLabel,
  color,
  cardSx: overrideCardSx,
}) {
  const theme = useTheme();
  const data = useMemo(
    () =>
      (Array.isArray(rows) ? rows : [])
        .map((row) => ({
          name: String(row.name || "—").trim() || "—",
          avgSec: Math.max(0, Number(row.avgSec) || 0),
          taskCount: Math.max(0, Number(row.taskCount) || 0),
        }))
        .filter((row) => row.avgSec > 0),
    [rows]
  );
  const barColor = color || theme.palette.info.main;
  const height = Math.max(240, data.length * 40 + 48);

  return (
    <Grid item xs={12}>
      <Card elevation={1} sx={overrideCardSx || cardSx}>
        <CardContent sx={{ pt: 2, pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            {hint}
          </Typography>
          {loading ? (
            <Skeleton variant="rounded" height={240} sx={{ borderRadius: 1 }} />
          ) : data.length ? (
            <Box sx={{ width: "100%", height }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis
                    type="number"
                    tickFormatter={(value) => secToHuman(value)}
                    allowDecimals={false}
                  />
                  <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 12 }} />
                  <ChartTooltip
                    formatter={(value, _name, item) => {
                      const count = item?.payload?.taskCount;
                      const label = count
                        ? `${secToHuman(value)} · ${BR(count)}`
                        : secToHuman(value);
                      return [label, axisLabel];
                    }}
                  />
                  <Bar dataKey="avgSec" name={axisLabel} fill={barColor} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
              {emptyLabel}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
}

function analyticsTableSx(theme) {
  const headBg = alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.14 : 0.07);
  return {
    "& .MuiTableCell-head": {
      fontWeight: 600,
      fontSize: "0.75rem",
      letterSpacing: "0.02em",
      color: theme.palette.text.secondary,
      backgroundColor: headBg,
      borderBottom: `1px solid ${theme.palette.divider}`,
      whiteSpace: "nowrap",
      py: 1.125,
    },
    "& .MuiTableCell-body": {
      fontSize: "0.8125rem",
      py: 1.125,
      borderColor: theme.palette.divider,
      verticalAlign: "middle",
    },
    "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even) .MuiTableCell-body": {
      backgroundColor: alpha(theme.palette.action.hover, theme.palette.mode === "dark" ? 0.06 : 0.04),
    },
  };
}

export function TaskAnalyticsPanels({ teamTasks, loading, variant = "board" }) {
  const theme = useTheme();
  ensureNamespaceLoadedSync("settings");
  ensureNamespaceLoadedSync("chat");
  const { t } = useTranslation("settings");
  const { t: tChat } = useTranslation("chat");
  const { t: tBoard } = useTranslation("teamTasks");
  const mine = variant === "user";
  const workspace = variant === "workspace";
  const taskSummary = teamTasks?.summary || {};
  const series = useMemo(() => {
    const p = theme.palette;
    return [
      p.primary.main,
      p.secondary?.main ?? p.info.main,
      p.warning.main,
      p.success.main,
      p.error.main,
      p.info.main,
      darken(p.primary.main, 0.2),
      lighten(p.primary.main, 0.35),
    ];
  }, [theme]);
  const tableSx = useMemo(() => analyticsTableSx(theme), [theme]);
  const statusRows = useMemo(() => resolveTaskStatusSummary(teamTasks, t), [teamTasks, t]);
  const statusTotal = statusRows.reduce((sum, row) => sum + Number(row.value || 0), 0);
  const receivedHours = teamTasks?.receivedByHour || [];
  const completedHours = teamTasks?.completedByHour || [];
  const receivedPeak = peakHourSummary(receivedHours);
  const completedPeak = peakHourSummary(completedHours);
  const maxReceived = Math.max(1, ...receivedHours.map((row) => Number(row.value) || 0));
  const maxCompleted = Math.max(1, ...completedHours.map((row) => Number(row.value) || 0));

  if (!teamTasks && !loading) return null;

  return (
    <Grid container spacing={2}>
      <KpiCard
        loading={loading}
        icon={<AssignmentIcon />}
        title={t("userOperatorAnalytics.kpiTasksPeriod")}
        value={BR(taskSummary.total ?? 0)}
        color={series[0]}
        sub={t("userOperatorAnalytics.kpiTasksPeriodSub")}
      />
      <KpiCard
        loading={loading}
        icon={<TimelapseIcon />}
        title={t("userOperatorAnalytics.kpiCycle")}
        value={taskSummary.avgCycleSec == null ? "—" : secToHuman(taskSummary.avgCycleSec)}
        color={series[1]}
        sub={
          Number(teamTasks?.avgWaitingSec) > 0
            ? t("userOperatorAnalytics.kpiCycleWaitSub", { time: secToHuman(teamTasks.avgWaitingSec) })
            : t("userOperatorAnalytics.kpiCycleSub")
        }
      />
      <KpiCard
        loading={loading}
        icon={<CheckCircleOutlineIcon />}
        title={t("userOperatorAnalytics.kpiOnTime")}
        value={taskSummary.onTimePct == null ? "—" : `${taskSummary.onTimePct}%`}
        color={series[3]}
        sub={
          Number(taskSummary.withDue) > 0
            ? t("userOperatorAnalytics.kpiOnTimeSub", {
                onTime: BR(taskSummary.noPrazo ?? 0),
                withDue: BR(taskSummary.withDue ?? 0),
              })
            : t("userOperatorAnalytics.kpiOnTimeEmpty")
        }
      />
      <KpiCard
        loading={loading}
        icon={<WarningAmberIcon />}
        title={t("userOperatorAnalytics.kpiOverdueNow")}
        value={BR(taskSummary.atrasadasAgora ?? 0)}
        color={series[4]}
        sub={t("userOperatorAnalytics.kpiOverdueNowSub")}
      />
      <KpiCard
        loading={loading}
        icon={<AssignmentTurnedInIcon />}
        title={t("userOperatorAnalytics.kpiTasksCompleted")}
        value={BR(teamTasks?.tasksCompleted ?? 0)}
        color={series[0]}
        sub={tChat("analytics.selectedPeriod")}
      />
      <KpiCard
        loading={loading}
        icon={<TimerIcon />}
        title={t("userOperatorAnalytics.kpiTotalEffort")}
        value={secToHuman(teamTasks?.totalEffortSec)}
        color={series[2]}
        sub={t("userOperatorAnalytics.kpiTotalEffortSub")}
      />
      <KpiCard
        loading={loading}
        icon={<AccessTimeIcon />}
        title={t("userOperatorAnalytics.kpiAvgEffort")}
        value={secToHuman(teamTasks?.avgEffortSec)}
        color={series[5]}
        sub={t("userOperatorAnalytics.kpiAvgEffortSub")}
      />

      <Grid item xs={12}>
        <Card elevation={1} sx={cardSx}>
          <CardContent sx={{ pt: 2, pb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t("userOperatorAnalytics.tasksStatusSummary")}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              {t("userOperatorAnalytics.tasksStatusSummaryHint")}
              {Number(taskSummary.obrigatoriasAbertas) > 0
                ? ` ${t("userOperatorAnalytics.requiredOpenNote", { count: BR(taskSummary.obrigatoriasAbertas) })}`
                : ""}
            </Typography>
            {loading ? (
              <Grid container spacing={1.5}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Grid item xs={12} sm={6} md key={i}>
                    <Skeleton variant="rounded" height={88} sx={{ borderRadius: 1 }} />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Grid container spacing={1.5}>
                {statusRows.map((row) => {
                  const Icon = row.icon;
                  const color = theme.palette.status?.[row.paletteKey] || theme.palette.primary.main;
                  const pct = statusTotal > 0 ? Math.round((Number(row.value) / statusTotal) * 100) : 0;
                  return (
                    <Grid item xs={12} sm={6} md key={row.key} sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "100%",
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                          px: 1.5,
                          py: 1.25,
                          bgcolor: alpha(color, theme.palette.mode === "dark" ? 0.12 : 0.06),
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                          <Avatar sx={{ bgcolor: color, width: 32, height: 32 }}>
                            <Icon sx={{ fontSize: 18 }} />
                          </Avatar>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }} noWrap>
                            {row.label}
                          </Typography>
                        </Stack>
                        <Typography variant="h5" sx={{ color, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                          {BR(row.value)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {pct}% · {tChat("analytics.selectedPeriod")}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </CardContent>
        </Card>
      </Grid>

      <DailyFlowChart
        title={t("userOperatorAnalytics.taskDailyTitle")}
        hint={mine ? t("userOperatorAnalytics.taskDailyHint") : tBoard("boardAnalyticsDailyHint")}
        data={teamTasks?.dailyFlow || []}
        receivedLabel={t("userOperatorAnalytics.dailyReceived")}
        completedLabel={t("userOperatorAnalytics.dailyCompleted")}
        receivedColor={series[0]}
        completedColor={series[3]}
      />
      <StageTimeAvgChart
        loading={loading}
        rows={teamTasks?.avgTimeByStage || []}
        title={t("userOperatorAnalytics.stageTimeAvgTitle")}
        hint={t("userOperatorAnalytics.stageTimeAvgHint")}
        emptyLabel={t("userOperatorAnalytics.stageTimeAvgEmpty")}
        axisLabel={t("userOperatorAnalytics.stageTimeAvgAxis")}
        color={series[5]}
      />
      <HourDistributionCard
        title={t("userOperatorAnalytics.tasksReceivedHours")}
        hint={mine ? t("userOperatorAnalytics.tasksReceivedHoursHint") : tBoard("boardAnalyticsReceivedHint")}
        peakText={receivedPeak ? t("userOperatorAnalytics.taskHourPeak", { hours: receivedPeak.label, count: BR(receivedPeak.max) }) : null}
        rows={receivedHours}
        maxValue={maxReceived}
        emptyLabel={t("userOperatorAnalytics.emptyTaskHour")}
        colHour={t("userOperatorAnalytics.colHour")}
        colDistribution={t("userOperatorAnalytics.colDistribution")}
        colQty={t("userOperatorAnalytics.colQty")}
        tableSx={tableSx}
      />
      <HourDistributionCard
        title={t("userOperatorAnalytics.tasksCompletedHours")}
        hint={mine ? t("userOperatorAnalytics.tasksCompletedHoursHint") : tBoard("boardAnalyticsCompletedHint")}
        peakText={completedPeak ? t("userOperatorAnalytics.taskHourPeak", { hours: completedPeak.label, count: BR(completedPeak.max) }) : null}
        rows={completedHours}
        maxValue={maxCompleted}
        emptyLabel={t("userOperatorAnalytics.emptyTaskHour")}
        colHour={t("userOperatorAnalytics.colHour")}
        colDistribution={t("userOperatorAnalytics.colDistribution")}
        colQty={t("userOperatorAnalytics.colQty")}
        tableSx={tableSx}
      />

      {workspace ? (
        <Grid item xs={12}>
          <Card elevation={1} sx={cardSx}>
            <CardContent sx={{ pt: 2, pb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("dashboardPage.tasksByUserTitle")}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                {t("dashboardPage.tasksByUserHint")}
              </Typography>
              {(teamTasks?.byUser || []).length ? (
                <Box sx={{ width: "100%", height: Math.max(280, (teamTasks.byUser || []).length * 36) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={(teamTasks.byUser || []).map((row) => ({
                        ...row,
                        name: row.userId ? row.name || "—" : t("dashboardPage.tasksByUserUnassigned"),
                      }))}
                      layout="vertical"
                      margin={{ top: 8, right: 16, left: 8, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis type="category" dataKey="name" width={148} tick={{ fontSize: 12 }} />
                      <ChartTooltip />
                      <Legend />
                      <Bar dataKey="total" name={t("dashboardPage.tasksByUserTotal")} fill={series[0]} radius={[0, 4, 4, 0]} />
                      <Bar dataKey="completed" name={t("dashboardPage.tasksByUserCompleted")} fill={series[3]} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                  {t("dashboardPage.tasksByUserEmpty")}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      ) : (
      <Grid item xs={12}>
        <Card elevation={1} sx={cardSx}>
          <CardContent sx={{ pt: 2, pb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t("userOperatorAnalytics.tasksCompletedList")}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              {t("userOperatorAnalytics.tasksWithDurationNote", {
                with: Number(teamTasks?.tasksWithDuration || 0),
                total: Number(teamTasks?.tasksCompleted || 0),
              })}
            </Typography>
            <TableContainer variant="outlined" sx={{ bgcolor: "background.paper", borderRadius: 1, maxHeight: 420 }}>
              <Table size="small" stickyHeader sx={tableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("userOperatorAnalytics.colTask")}</TableCell>
                    {mine || workspace ? (
                      <TableCell>{t("userOperatorAnalytics.colBoard")}</TableCell>
                    ) : null}
                    {!mine ? <TableCell>{tBoard("boardAnalyticsColAssignee")}</TableCell> : null}
                    {!mine ? <TableCell>{tBoard("boardAnalyticsColColumn")}</TableCell> : null}
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{t("userOperatorAnalytics.colCompletedAt")}</TableCell>
                    <TableCell align="right">{t("userOperatorAnalytics.colCycle")}</TableCell>
                    <TableCell align="right">{t("userOperatorAnalytics.colWaiting")}</TableCell>
                    <TableCell align="right">{t("userOperatorAnalytics.colReopens")}</TableCell>
                    <TableCell align="right">{t("userOperatorAnalytics.colEffort")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(teamTasks?.tasks || []).length ? (
                    (teamTasks.tasks || []).map((task) => (
                      <TableRow key={task.id} hover>
                        <TableCell>
                          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatTaskDisplayName(task.id, task.title)}</Typography>
                            {task.isRequired ? (
                              <Chip size="small" label={t("userOperatorAnalytics.taskRequired")} color="warning" variant="outlined" sx={{ height: 22, fontWeight: 600 }} />
                            ) : null}
                          </Stack>
                        </TableCell>
                        {mine || workspace ? <TableCell>{task.boardName || "—"}</TableCell> : null}
                        {!mine ? <TableCell>{task.assigneeNames || "—"}</TableCell> : null}
                        {!mine ? <TableCell>{task.columnName || "—"}</TableCell> : null}
                        <TableCell sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                          {task.completedAt ? formatDateTimePtBR(task.completedAt) : "—"}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                          {task.cycleSec != null ? secToHuman(task.cycleSec) : "—"}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                          {Number(task.waitingSec) > 0 ? secToHuman(task.waitingSec) : "—"}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                          {Number(task.reopenCount) > 0 ? BR(task.reopenCount) : "—"}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                          {task.estimatedDuration || (task.effortSec != null ? secToHuman(task.effortSec) : "—")}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={mine ? 7 : workspace ? 9 : 8} align="center" sx={{ py: 3, color: "text.secondary" }}>
                        <Typography variant="body2">{t("userOperatorAnalytics.emptyTasks")}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
      )}
    </Grid>
  );
}

const fieldSx = { minWidth: { xs: "100%", sm: 180 } };

export function MyTasksAnalytics() {
  ensureNamespaceLoadedSync("settings");
  const { t } = useTranslation("settings");
  const { t: tBoard } = useTranslation("teamTasks");
  const [start, setStart] = useState(() => dayjs().startOf("month"));
  const [end, setEnd] = useState(() => dayjs().endOf("month"));
  const startStr = start?.isValid?.() ? start.format("YYYY-MM-DD") : "";
  const endStr = end?.isValid?.() ? end.format("YYYY-MM-DD") : "";
  const periodOk = Boolean(startStr && endStr && startStr <= endStr);

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    ["myTaskAnalytics", startStr, endStr],
    () => fetchWorkspaceTaskAnalytics({ start: startStr, end: endStr, userId: "me" }),
    { enabled: periodOk, keepPreviousData: true }
  );

  const teamTasks = data?.teamTasks || null;
  const errMsg =
    (typeof error?.message === "string" && error.message) ||
    (error ? tBoard("inboxAnalyticsLoadError") : "");

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack spacing={2} sx={{ width: "100%", minWidth: 0 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ sm: "center" }}>
          <DatePicker
            label={t("userOperatorAnalytics.start")}
            value={start}
            onChange={(v) => v && setStart(v.startOf("day"))}
            slotProps={{ textField: { size: "small", sx: fieldSx } }}
          />
          <DatePicker
            label={t("userOperatorAnalytics.end")}
            value={end}
            onChange={(v) => v && setEnd(v.endOf("day"))}
            slotProps={{ textField: { size: "small", sx: fieldSx } }}
          />
          <Button variant="outlined" size="small" onClick={() => refetch()} disabled={!periodOk || isFetching}>
            {isFetching ? <CircularProgress size={16} /> : t("userOperatorAnalytics.apply")}
          </Button>
        </Stack>
        {errMsg ? <Alert severity="error">{errMsg}</Alert> : null}
        {isFetching ? <LinearProgress /> : null}
        <TaskAnalyticsPanels
          teamTasks={teamTasks}
          loading={isLoading && !teamTasks}
          variant="user"
        />
      </Stack>
    </LocalizationProvider>
  );
}
